import React, { useState, useCallback, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { AlertCircle } from 'lucide-react';

const fmt = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const COLORS = ['#E8721A', '#15140F', '#1D5FCC', '#0E8855', '#B07003', '#7B3A78', '#14b8a6', '#ec4899', '#6366f1', '#64748b'];

const ChargeBreakdownChart = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/finance/charge-breakdown', { params: { year } });
      setData(res.data);
    } catch {
      setError('Impossible de charger la répartition des charges.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const breakdown = (data?.breakdown || []).filter((b) => b.amount > 0);

  const chartData = breakdown.map((b) => ({
    name: b.label,
    value: b.amount,
  }));

  return (
    <div className="space-y-4">
      {/* Year selector */}
      <div className="flex items-center gap-2">
        {years.map((y) => (
          <Button
            key={y}
            size="sm"
            variant={y === year ? 'default' : 'outline'}
            onClick={() => setYear(y)}
          >
            {y}
          </Button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Chargement...</div>
      ) : breakdown.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">Aucune charge pour {year}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Donut chart */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Structure des coûts — {year}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={110}
                    paddingAngle={2}
                    label={false}
                  >
                    {chartData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => fmt(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #ECE7DC', fontSize: '13px' }} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: '11px', lineHeight: '1.8' }}
                  />
                  {/* Center label */}
                  <text x="40%" y="47%" textAnchor="middle" fontSize="11" fill="#6B6760" fontWeight="500">Coûts totaux</text>
                  <text x="40%" y="55%" textAnchor="middle" fontSize="18" fontWeight="800" fill="#15140F">{fmt(data?.grand_total)}</text>
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">
                Détail — Total : {fmt(data?.grand_total)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {breakdown.map((b, idx) => (
                  <div key={b.key} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-sm shrink-0"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="text-sm text-foreground">{b.label}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-foreground">{fmt(b.amount)}</span>
                      <span className="text-xs text-muted-foreground ml-2">({b.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChargeBreakdownChart;
